import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box, Button, Card, CardContent, Chip, CircularProgress, Divider, Stack, Switch,
  Typography,
} from '@mui/material';
import {
  PlayCircleOutline as CallIcon,
  ConfirmationNumber as TokenIcon,
} from '@mui/icons-material';
import toast from 'react-hot-toast';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/** The waiting room is only as current as this screen, so it refreshes itself. */
const POLL_MS = 20000;

/**
 * The clinician's two controls over their own line: whether they are seeing patients
 * at all, and calling the next token forward.
 *
 * "On a break" is not cosmetic — a queue that keeps calling people while the doctor is
 * at lunch tells the waiting room it is their turn when nobody is there. Switching off
 * deliberately does NOT end the consultation in progress; stepping out for a moment is
 * not the same as finishing with the patient in front of you.
 */
export default function ProviderQueueControl() {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/v1/queue/me/state`);
      setState(data);
    } catch {
      setState(null);        // never let this break the dashboard around it
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  const setPresence = async (on) => {
    setBusy(true);
    try {
      const { data } = await axios.put(`${API_URL}/api/v1/queue/me/presence`, {
        is_seeing_patients: on,
      });
      setState(data);
      toast.success(on ? 'You are seeing patients' : 'You are on a break');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not change that');
    } finally {
      setBusy(false);
    }
  };

  const callNext = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/v1/queue/me/call-next`);
      setState(data);
      toast.success(`Now with ${data.current?.patient_name || 'the next patient'}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not call the next patient');
    } finally {
      setBusy(false);
    }
  };

  // Nothing to show before the first load, or where the account holds no queue.
  if (loading || !state) return null;

  const { is_seeing_patients: on, current, next, waiting } = state;

  return (
    <Card variant="outlined" sx={{ mb: 3, borderColor: on ? 'success.light' : 'divider' }}>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
          <TokenIcon color={on ? 'success' : 'disabled'} />
          <Typography variant="h6" sx={{ flexGrow: 1 }}>Your queue</Typography>
          <Typography variant="body2" color="text.secondary">
            {on ? 'Seeing patients' : 'On a break'}
          </Typography>
          <Switch
            checked={!!on}
            disabled={busy}
            onChange={(e) => setPresence(e.target.checked)}
            inputProps={{ 'aria-label': 'Seeing patients' }}
          />
        </Stack>

        {!on && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Your patients are told you are on a break. Anyone already with you stays with
            you — this only stops the queue calling the next person forward.
          </Typography>
        )}

        <Divider sx={{ my: 2 }} />

        <Stack direction="row" spacing={3} sx={{ mb: 2, flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">With you now</Typography>
            <Typography variant="h5" fontWeight={700}>
              {current?.token || '—'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {current?.patient_name || 'Nobody yet'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Next</Typography>
            <Typography variant="h5" fontWeight={700}>
              {next?.token || '—'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {next?.patient_name || 'Queue empty'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Waiting</Typography>
            <Typography variant="h5" fontWeight={700}>{waiting}</Typography>
            <Chip size="small" variant="outlined"
              label={waiting === 1 ? '1 patient' : `${waiting} patients`} />
          </Box>
        </Stack>

        <Button
          variant="contained"
          startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <CallIcon />}
          disabled={busy || waiting === 0}
          onClick={callNext}
        >
          {waiting === 0
            ? 'Nobody waiting'
            : `Call next patient${next?.token ? ` (${next.token})` : ''}`}
        </Button>
        {current && waiting > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            This finishes with {current.patient_name} and calls {next?.token} through.
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}
